import {
  type BaseField,
  CUSTOMER_BALANCE,
  type Customer,
  type CustomerBalance,
  DecisionStatusEnum,
  EMPTY_ARRAY,
  type ExecutionReport,
  type FieldData,
  MultilegReportingTypeEnum,
  type Order,
  OrderMarketStatusEnum,
  type OrderStrategy,
  type ParameterLike,
  ParameterTypeEnum,
  type Position,
  type PositionUpdateSourceEnum,
  PresenceEnum,
  ProductTypeEnum,
  ReduceFirstEnum,
  ReduceOnlyEnum,
  type Security,
  getPositionAmount,
  getPositionID,
  isFuture,
  isMultileg,
  isPerpetualSwap,
  useObservableValue,
  useSubscription,
  wsScanToMap,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { map } from 'rxjs';
import { MULTILEG_MARKET_ACCOUNT } from '../../../providers/OMSContext.types';
import type { MarketSelectorItem } from '../../MarketSelector/types';
import { isMarketSelectorItemSelectable } from '../../MarketSelector/utils/items';
import { mapParameterToField } from '../SalesOrder/Strategies';
import type { OMSSettingsState } from '../types';

/**
 * Determines whether modify requests should send `Duration` instead of converting to `EndTime`.
 * `Staged` orders always send duration; `WaitingForStartTime` sends duration only when the feature flag is enabled.
 */
export function shouldSendDurationOnModify({
  decisionStatus,
  enableDurationSubmission,
}: {
  decisionStatus?: DecisionStatusEnum;
  enableDurationSubmission: boolean;
}): boolean {
  return (
    decisionStatus === DecisionStatusEnum.Staged ||
    (decisionStatus === DecisionStatusEnum.WaitingForStartTime && enableDurationSubmission)
  );
}

type InitializeStrategyParamsResult = { [key: string]: BaseField<FieldData, any> };
// Ideally we will have one initializeStrategyParams for every form but splitting Order form & Sales order form
// for now as both have different validations
//[ UI-4182] - Change Strategy Qty fields validation in order form
export const initializeStrategyParams = ({
  security,
  strategy,
  settings,
  excludedParams = EMPTY_ARRAY,
  isModify,
}: {
  strategy: OrderStrategy | undefined;
  security: Security | undefined;
  settings: OMSSettingsState;
  excludedParams?: string[];
  isModify?: boolean;
}): InitializeStrategyParamsResult => {
  const params: InitializeStrategyParamsResult = {};
  const { defaultPriceProtection } = settings;

  if (strategy) {
    strategy.Parameters
      // Only TopLevelHidden param that exists seem to be OrdType and only applicable on Market strategy -- and we can handle that manually
      .filter(param => param.Presence !== PresenceEnum.TopLevelHidden && param.Presence !== PresenceEnum.Hidden)
      .filter(param => !excludedParams.includes(param.Type))
      .forEach(param => {
        const parameter = { ...param, DefaultValue: getParamDefaultValue(param, isModify, settings) };

        params[parameter.Name] = mapParameterToField(
          parameter,
          security?.MinPriceIncrement,
          undefined, // [UI-4182] Skip NumericField scale validation as we validate all Qty fields in validateStrategyParams function in OrderSlice.ts
          defaultPriceProtection,
          isModify,
          strategy
        );
      });
  }

  return params;
};

export function getHedgingSecurities(security: Security | undefined, securitiesList: Security[]): Security[] {
  if (!security || security.ProductType !== ProductTypeEnum.Option) {
    return [];
  }
  return securitiesList.filter(s => s.BaseCurrency === security.BaseCurrency && (isPerpetualSwap(s) || isFuture(s)));
}

// Allow to overwrite some strategy parameters default values with user defined settings
function getParamDefaultValue(param: ParameterLike, isModify: boolean | undefined, settings: OMSSettingsState) {
  if (isModify) {
    return param.DefaultValue;
  }

  const { enableReduceFirst, enableReduceOnly } = settings;

  switch (param.Type) {
    case ParameterTypeEnum.ReduceOnly:
      return enableReduceOnly ? ReduceOnlyEnum.Enabled : param.DefaultValue;
    case ParameterTypeEnum.ReduceFirst:
      return enableReduceFirst ? ReduceFirstEnum.Enabled : param.DefaultValue;
    default:
      return param.DefaultValue;
  }
}

export const useCustomerSpecificBalances = (customer: Customer | undefined, tag: string) => {
  const customerBalanceRequest = useMemo(() => {
    if (!customer?.Name) {
      return null;
    }
    return {
      name: CUSTOMER_BALANCE,
      Counterparties: [customer?.Name],
      tag: tag,
    };
  }, [customer?.Name, tag]);

  const { data: specificCustomerBalanceObs } = useSubscription<CustomerBalance>(customerBalanceRequest);
  const specificCustomerBalances = useObservableValue(
    () =>
      specificCustomerBalanceObs.pipe(
        wsScanToMap({
          getUniqueKey: customerBalance => customerBalance.rowID,
          newMapEachUpdate: false,
        }),
        map(map => Array.from(map.values()))
      ),
    [specificCustomerBalanceObs]
  );

  return specificCustomerBalances;
};

/**
 * Given the entire array of all available markets, and the old and new market selection, determines if this change in market selection
 * should result in the selected aggregation to be cleared
 *
 * The Aggregation will be cleared if either: 1) newMarkets is empty, 2) newMarkets is not a subset of oldMarkets, and 3) any of the markets removed from selection
 * are still available for selection
 */
export function shouldClearAggregation({
  newMarkets,
  oldMarkets,
  items,
}: {
  newMarkets: string[];
  oldMarkets: string[];
  items: MarketSelectorItem[];
}): boolean {
  // 1) An empty selection surely cannot represent an aggregation
  if (newMarkets.length === 0) {
    return true;
  }

  // 2) make sure newMarkets is a subset of oldMarkets
  const oldMarketsSet = new Set(oldMarkets);
  const newMarketsIsSubset = !newMarkets.some(newMarket => !oldMarketsSet.has(newMarket));
  if (!newMarketsIsSubset) {
    return true;
  }

  // 3) make sure no removed markets are available
  const availableMarketsSet = new Set<string>();
  for (const item of items) {
    if (item.marketAccount != null && isMarketSelectorItemSelectable(item)) {
      availableMarketsSet.add(item.marketAccount.Name);
    }
  }

  const newMarketsSet = new Set(newMarkets);
  const removedMarkets = oldMarkets.filter(oldMarket => !newMarketsSet.has(oldMarket));
  const anyRemovedMarketStillAvailable = removedMarkets.some(removedMarket => availableMarketsSet.has(removedMarket));
  if (anyRemovedMarketStillAvailable) {
    return true;
  }

  return false;
}

/**
 * Given an Order or Er, will extract the leg market accounts, ul leg market accounts and leg initiating parameters into
 * separate properties for you to use
 */
export function getLegMarketAccountsFromOrder(order: Order | ExecutionReport, security: Security) {
  const ulLegMarketAccounts: string[] = [];
  const ulSummary = order.LegSummary?.find(
    leg => leg.MultilegReportingType === MultilegReportingTypeEnum.Parent && leg.Symbol !== security.Symbol
  );

  const legMarketAccounts: [string[], string[]] = [[], []];

  order.Markets.filter(m => m.MarketStatus !== OrderMarketStatusEnum.Disabled).forEach(orderMarket => {
    if (!orderMarket.MarketAccount || orderMarket.LegSummaryIndex == null) {
      return;
    }

    const leg = order.LegSummary?.at(orderMarket.LegSummaryIndex);
    if (leg && leg.MultilegReportingType === MultilegReportingTypeEnum.Leg) {
      legMarketAccounts.at(leg.LegIndex)?.push(orderMarket.MarketAccount);
    } else if (ulSummary && ulSummary.Symbol === orderMarket.Symbol) {
      ulLegMarketAccounts.push(orderMarket.MarketAccount);
    }
  });

  const legInitiatings: boolean[] | undefined = order.MultilegParams?.LegParams.map(p => p.Initiating as boolean);
  const legHaircutRatios: string[] | undefined = order.MultilegParams?.LegParams.map(p =>
    String(p.HaircutRatio ?? '0')
  );

  return { legMarketAccounts, ulLegMarketAccounts, legInitiatings, legHaircutRatios };
}

export function getOrderMarketAccountsFromOrder(order: Order | ExecutionReport, security: Security) {
  if (isMultileg(security)) {
    return [MULTILEG_MARKET_ACCOUNT];
  }

  return compact(
    order.Markets.filter(m => m.MarketStatus !== OrderMarketStatusEnum.Disabled).map(m => m.MarketAccount)
  );
}

/** In the OrderSlice, we create a static Position request. */
export const ORDERSLICE_POSITION_REQUEST_PARAMS: { AssetTypes: ProductTypeEnum[]; Source: PositionUpdateSourceEnum } = {
  AssetTypes: ['Future', 'PerpetualSwap', 'Option'],
  Source: 'Gateway',
};

/**
 * Performs the correct lookup in the positions map from streamingDataSlice.endpoints.getPositions based on the
 * provided marketAccount name and symbol for the order slice
 */
export function getOrderSlicePosition(
  positions: Map<string, Position> | undefined,
  marketAccount: string | undefined,
  symbol: string | undefined
) {
  if (!positions || !marketAccount || !symbol) {
    return undefined;
  }

  return positions.get(getPositionID(ORDERSLICE_POSITION_REQUEST_PARAMS.Source, marketAccount, symbol, undefined));
}

/**
 * Given a set of symbols and market accounts, return the position amounts for each combo.
 */
export function getPositionAmounts(
  marketAccountAndSymbols: { marketAccount: string; symbol: string }[],
  positions: Map<string, Position>,
  securitiesBySymbol: Map<string, Security>,
  positionSource: PositionUpdateSourceEnum
): { value: string; currency: string | undefined; marketAccount: string; symbol: string }[] {
  const amounts = marketAccountAndSymbols.map(symbolAndMA => {
    const position = positions.get(
      getPositionID(positionSource, symbolAndMA.marketAccount, symbolAndMA.symbol, undefined)
    );
    return {
      ...getPositionAmount(position?.Amount || '0.0', securitiesBySymbol.get(symbolAndMA.symbol)),
      marketAccount: symbolAndMA.marketAccount,
      symbol: symbolAndMA.symbol,
    };
  });
  return amounts;
}
